import { Check, Flame, Target } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { ReadHistory } from '@/api/type'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'

type Props = {
  history: ReadHistory[]
  isLoading: boolean
  selectedDate: Date
}

type Goal = {
  id: string
  type: 'daily_minutes' | 'daily_sessions' | 'streak'
  target: number
  label: string
}

const DEFAULT_GOALS: Goal[] = [
  { id: 'daily_minutes', type: 'daily_minutes', target: 30, label: '1日の読書時間' },
  { id: 'daily_sessions', type: 'daily_sessions', target: 2, label: '1日のセッション数' },
  { id: 'streak', type: 'streak', target: 7, label: '連続読書日数' },
]

// バッジ定義
const BADGES = [
  {
    id: 'first_read',
    label: '初読書',
    description: '最初の読書を記録',
    icon: '📖',
    condition: (h: ReadHistory[]) => h.length > 0,
  },
  {
    id: 'streak_7',
    label: '7日連続',
    description: '7日連続で読書',
    icon: '🔥',
    condition: (_: ReadHistory[], streak: number) => streak >= 7,
  },
  {
    id: 'streak_30',
    label: '30日連続',
    description: '30日連続で読書',
    icon: '🏆',
    condition: (_: ReadHistory[], streak: number) => streak >= 30,
  },
  {
    id: 'sessions_10',
    label: '10セッション',
    description: '10回読書',
    icon: '⭐',
    condition: (h: ReadHistory[]) => h.length >= 10,
  },
  {
    id: 'sessions_50',
    label: '50セッション',
    description: '50回読書',
    icon: '🌟',
    condition: (h: ReadHistory[]) => h.length >= 50,
  },
  {
    id: 'sessions_100',
    label: '100セッション',
    description: '100回読書',
    icon: '💫',
    condition: (h: ReadHistory[]) => h.length >= 100,
  },
  {
    id: 'hours_10',
    label: '10時間達成',
    description: '累計10時間読書',
    icon: '⏰',
    condition: (h: ReadHistory[]) => {
      const totalMinutes = h.reduce((sum, item) => {
        const start = new Date(item.started_at).getTime()
        const end = new Date(item.ended_at).getTime()
        return sum + (end - start) / 60000
      }, 0)
      return totalMinutes >= 600
    },
  },
]

export const GoalsView = ({ history, isLoading, selectedDate: _selectedDate }: Props) => {
  const [goals, setGoals] = useState<Goal[]>(() => {
    const saved = localStorage.getItem('activity_goals')
    return saved ? (JSON.parse(saved) as Goal[]) : DEFAULT_GOALS
  })
  const [editingGoal, setEditingGoal] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')

  // 目標をローカルストレージに保存
  useEffect(() => {
    localStorage.setItem('activity_goals', JSON.stringify(goals))
  }, [goals])

  // 統計計算
  const stats = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // 今日の履歴
    const todayHistory = history.filter((item) => {
      const date = new Date(item.started_at)
      date.setHours(0, 0, 0, 0)
      return date.getTime() === today.getTime()
    })

    // 今日の読書時間（分）
    const todayMinutes = todayHistory.reduce((sum, item) => {
      const start = new Date(item.started_at).getTime()
      const end = new Date(item.ended_at).getTime()
      return sum + (end - start) / 60000
    }, 0)

    // 連続日数
    const dates = [...new Set(history.map((item) => new Date(item.started_at).toDateString()))].sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime(),
    )

    let streakDays = 0
    for (let i = 0; i < dates.length; i++) {
      const expectedDate = new Date(today)
      expectedDate.setDate(expectedDate.getDate() - i)
      const dateStr = dates.at(i)
      if (!dateStr) break
      const currentDate = new Date(dateStr)
      currentDate.setHours(0, 0, 0, 0)
      expectedDate.setHours(0, 0, 0, 0)

      if (currentDate.getTime() === expectedDate.getTime()) {
        streakDays++
      } else if (i === 0) {
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)
        yesterday.setHours(0, 0, 0, 0)
        if (currentDate.getTime() === yesterday.getTime()) {
          streakDays++
        } else {
          break
        }
      } else {
        break
      }
    }

    return {
      todayMinutes: Math.round(todayMinutes),
      todaySessions: todayHistory.length,
      streakDays,
    }
  }, [history])

  // 目標達成度を計算
  const getProgress = (goal: Goal): { current: number; percentage: number } => {
    let current = 0
    switch (goal.type) {
      case 'daily_minutes':
        current = stats.todayMinutes
        break
      case 'daily_sessions':
        current = stats.todaySessions
        break
      case 'streak':
        current = stats.streakDays
        break
    }
    return {
      current,
      percentage: Math.min((current / goal.target) * 100, 100),
    }
  }

  // 目標を更新
  const updateGoalTarget = (goalId: string) => {
    const value = parseInt(editValue, 10)
    if (isNaN(value) || value <= 0) return

    setGoals((prev) => prev.map((g) => (g.id === goalId ? { ...g, target: value } : g)))
    setEditingGoal(null)
    setEditValue('')
  }

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-48" />
      </div>
    )
  }

  return (
    <div className="p-4 space-y-6">
      {/* 今日の進捗 */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Target className="size-4" />
            今日の目標
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {goals.map((goal) => {
            const { current, percentage } = getProgress(goal)
            const isCompleted = percentage >= 100

            return (
              <div key={goal.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">{goal.label}</Label>
                  {editingGoal === goal.id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-20 h-7 text-sm"
                        min={1}
                      />
                      <Button size="sm" variant="ghost" className="h-7" onClick={() => updateGoalTarget(goal.id)}>
                        <Check className="size-4" />
                      </Button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setEditingGoal(goal.id)
                        setEditValue(goal.target.toString())
                      }}
                      className="text-sm text-muted-foreground hover:text-foreground"
                    >
                      {current} / {goal.target}
                      {goal.type === 'daily_minutes' ? '分' : goal.type === 'streak' ? '日' : '回'}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Progress value={percentage} className="flex-1" />
                  {isCompleted && <Check className="size-4 text-green-500" />}
                </div>
              </div>
            )
          })}
        </CardContent>
      </Card>

      {/* 連続記録 */}
      <Card className="bg-linear-to-br from-orange-500/20 to-red-500/20 border-orange-500/30">
        <CardContent className="p-6 flex items-center justify-center gap-4">
          <Flame className="size-12 text-orange-500" />
          <div>
            <div className="text-4xl font-bold">{stats.streakDays}</div>
            <div className="text-sm text-muted-foreground">日連続</div>
          </div>
        </CardContent>
      </Card>

      {/* バッジ */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">獲得バッジ</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-3">
            {BADGES.map((badge) => {
              const earned = badge.condition(history, stats.streakDays)
              return (
                <div
                  key={badge.id}
                  className={`flex flex-col items-center gap-1 p-2 rounded-lg ${earned ? 'bg-muted' : 'opacity-30'}`}
                  title={badge.description}
                >
                  <span className="text-2xl">{badge.icon}</span>
                  <span className="text-[10px] text-center leading-tight">{badge.label}</span>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
